import { MediaPicker } from '@/components/media/MediaPicker';
import type { MediaRow } from '@/routes/media/types';
import { Button, Chip, Input, Spinner } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { PublishingSidebar } from '@/components/editor/PublishingSidebar';
import { RichTextField } from '@/components/editor/RichTextField';
import { SlugField } from '@/components/form/SlugField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import { useResourceForm } from '@/lib/forms/useResourceForm';

import { CLUB_PLATFORMS, type ClubInput, type ClubPlatform, type ClubRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/clubs`.
 *
 * docs/01-INFORMATION-ARCHITECTURE.md §12 renders an ordered grid of cards, so
 * `sort_order` is the arrangement an editor comes here to make — and the list
 * is sorted by it, so they are looking at the thing they are changing.
 */
export function ClubsPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <List /> : <Editor recordId={rest} />;
}

function List() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();

  const columns: Column<ClubRow>[] = [
    {
      key: 'name',
      label: t('content.clubs.columns.name'),
      render: (row) => (
        <span className="font-semibold text-primary">{valueFor(row.name, locale)}</span>
      ),
    },
    {
      key: 'contact',
      label: t('content.clubs.columns.contact'),
      render: (row) => (
        <span className="text-caption text-secondary">
          {valueFor(row.contact_person, locale) || row.contact_email || '—'}
        </span>
      ),
    },
    {
      key: 'links',
      label: t('content.clubs.columns.links'),
      render: (row) => {
        const count = Object.keys(row.social_links).length;

        return <span className="text-secondary">{count === 0 ? '—' : count}</span>;
      },
    },
    {
      key: 'status',
      label: t('content.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'published' ? 'navy' : 'gold'}>
          {t(`editor.publishing.statuses.${row.status}`)}
        </Chip>
      ),
    },
    {
      key: 'sort_order',
      label: t('content.fields.order'),
      sortable: true,
      align: 'end',
      render: (row) => row.sort_order,
    },
  ];

  return (
    <ResourceListScreen
      resource="clubs"
      title={t('nav.items.clubs')}
      intro={t('content.clubs.intro')}
      columns={columns}
      labelFor={(row) => valueFor(row.name, locale)}
      defaultSort="sort_order"
      emptyTitle={t('content.clubs.empty.title')}
      emptyBody={t('content.clubs.empty.body')}
      newLabel={t('content.clubs.new')}
      deleteTitle={t('content.clubs.delete.title')}
      deleteBody={(label) => t('content.delete.body', { label })}
    />
  );
}

interface Fields {
  name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  body: TranslatedValue;
  cover_media_id: number | null;
  cover: MediaRow | null;
  contact_email: string;
  contact_person: TranslatedValue;
  social_links: Partial<Record<ClubPlatform, string>>;
  sort_order: number;
  status: ClubRow['status'];
  published_at: string | null;
  pending_review: boolean;
}

function Editor({ recordId }: { recordId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useAuth();

  const mayPublish = can('clubs.publish');

  const { form, values, record, isNew, isLoading, isSubmitting, formError, submit, guard } =
    useResourceForm<ClubRow, Fields, ClubInput>({
      resource: 'clubs',
      recordId,
      empty,
      toFields,
      toInput: (fields) => toInput(fields, mayPublish),
      errorFields: [
        'name',
        'slug',
        'description',
        'body',
        'cover_media_id',
        'contact_email',
        'contact_person',
        'sort_order',
        'status',
        'published_at',
      ],
    });

  const { register, setValue, formState } = form;

  if (isLoading) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-h2 font-extrabold text-primary">
          {isNew ? t('content.clubs.new') : t('content.clubs.edit')}
        </h1>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/clubs')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          <TranslatableField
            label={t('content.clubs.fields.name')}
            name="name"
            value={values.name}
            onChange={(next) => setValue('name', next, { shouldDirty: true })}
            error={formState.errors.name?.message}
          />

          <SlugField
            value={values.slug}
            onChange={(next) => setValue('slug', next, { shouldDirty: true })}
            original={record?.slug}
            published={values.status === 'published'}
            error={formState.errors.slug?.message}
          />

          {/* Required: §12's grid is a card per club, and the description *is*
              the card. A club without one renders as a title and white space. */}
          <TranslatableField
            label={t('content.fields.description')}
            hint={t('content.clubs.fields.descriptionHint')}
            name="description"
            multiline
            rows={3}
            value={values.description}
            onChange={(next) => setValue('description', next, { shouldDirty: true })}
            error={formState.errors.description?.message}
          />

          <RichTextField
            label={t('content.fields.body')}
            value={values.body}
            onChange={(next) => setValue('body', next, { shouldDirty: true })}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              {...register('contact_email')}
              type="email"
              label={t('content.clubs.fields.email')}
              error={formState.errors.contact_email?.message}
            />

            <MediaPicker
              label={t('content.fields.cover')}
              hint={t('content.fields.coverHint')}
              value={values.cover}
              onChange={(mediaId, media) => {
                setValue('cover_media_id', mediaId, { shouldDirty: true });
                setValue('cover', media, { shouldDirty: true });
              }}
              error={formState.errors.cover_media_id?.message}
            />

            <Input
              {...register('sort_order', { valueAsNumber: true })}
              type="number"
              min={0}
              label={t('content.fields.order')}
              hint={t('content.fields.orderHint')}
            />
          </div>

          <TranslatableField
            label={t('content.clubs.fields.person')}
            name="contact_person"
            value={values.contact_person}
            onChange={(next) => setValue('contact_person', next, { shouldDirty: true })}
            required={false}
          />

          {/*
            One box per platform rather than a free-form key/value editor. §12
            draws an icon per known platform, so a typed key draws nothing —
            and the server refuses an unknown one, which would be a 422 about a
            field the editor invented.
          */}
          <fieldset className="space-y-4 rounded-card border border-subtle p-4">
            <legend className="px-1 text-caption font-extrabold tracking-wide text-muted">
              {t('content.clubs.social')}
            </legend>

            <div className="grid gap-4 sm:grid-cols-2">
              {CLUB_PLATFORMS.map((platform) => (
                <Input
                  key={platform}
                  type="url"
                  label={t(`content.clubs.platforms.${platform}`)}
                  value={values.social_links[platform] ?? ''}
                  onChange={(event) =>
                    setValue(
                      'social_links',
                      { ...values.social_links, [platform]: event.target.value },
                      { shouldDirty: true },
                    )
                  }
                />
              ))}
            </div>
          </fieldset>
        </div>

        <div className="space-y-4">
          <PublishingSidebar
            resource="clubs"
            status={values.status}
            onStatusChange={(status) => setValue('status', status, { shouldDirty: true })}
            publishedAt={values.published_at}
            onPublishedAtChange={(next) => setValue('published_at', next, { shouldDirty: true })}
            updatedAt={record?.updated_at}
            updatedBy={record?.updated_by}
            pendingReview={record?.pending_review}
            saving={isSubmitting}
          />

          <TranslationProgress
            fields={{
              name: { label: t('content.clubs.fields.name'), value: values.name },
              description: { label: t('content.fields.description'), value: values.description },
            }}
          />
        </div>
      </div>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function empty(): Fields {
  return {
    name: {},
    slug: {},
    description: {},
    body: {},
    cover_media_id: null,
    cover: null,
    contact_email: '',
    contact_person: {},
    social_links: {},
    sort_order: 0,
    status: 'draft',
    published_at: null,
    pending_review: false,
  };
}

function toFields(row: ClubRow): Fields {
  return {
    name: row.name,
    slug: row.slug,
    description: row.description,
    body: row.body,
    cover_media_id: row.cover_media_id ?? null,
    cover: row.cover ?? null,
    contact_email: row.contact_email ?? '',
    contact_person: row.contact_person,
    social_links: row.social_links,
    sort_order: row.sort_order,
    status: row.status,
    published_at: row.published_at,
    pending_review: row.pending_review,
  };
}

function toInput(form: Fields, mayPublish: boolean): ClubInput {
  /*
   * Emptied boxes are dropped here as well as on the server. Sending `''`
   * would be refused as an invalid URL, and the editor would get a 422 about a
   * platform they had deliberately cleared.
   */
  const links = Object.fromEntries(
    Object.entries(form.social_links).filter(([, url]) => (url ?? '').trim() !== ''),
  ) as Partial<Record<ClubPlatform, string>>;

  return {
    name: form.name,
    slug: form.slug,
    description: form.description,
    body: form.body,
    cover_media_id: form.cover_media_id,
    contact_email: form.contact_email.trim() === '' ? null : form.contact_email.trim(),
    contact_person: form.contact_person,
    social_links: links,
    sort_order: Number(form.sort_order),
    status: form.status,
    published_at: form.published_at,
    pending_review: mayPublish ? form.pending_review : true,
  };
}
